import { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'

// FIX 2 · endpoint de logging del front (Nico 18-jul-2026 · post-diag caso Marcela)
// Cada fallo silencioso del Brick queda registrado en signup_errors_log y dispara alerta WA
// al 2º error del mismo email en 15 min (via trigger tg_signup_errors_alertar).
const LOG_FRONT_ERROR_URL = 'https://xsviveqhkppmhncodhuv.supabase.co/functions/v1/log-front-error'
const SDK_LOAD_TIMEOUT_MS = 15000

// Session ID único por montaje del brick — útil para agrupar events cuando el user no está autenticado
function generarSessionId() {
  try { return crypto.randomUUID() } catch { return String(Date.now()) + Math.random().toString(36).slice(2) }
}

/**
 * MpTarjetaBrick — Componente que carga MP SDK JS y renderiza un CardPayment Brick para tokenizar
 * la tarjeta en el navegador del cliente. Evita el redirect al checkout hosted (bug ESC).
 *
 * Uso:
 *   <MpTarjetaBrick
 *     monto={1490}
 *     emailPagador="cliente@ejemplo.com"
 *     userId={user.id}                              // opcional pero recomendado para logging
 *     sessionId={sessionIdWizard}                   // opcional — si viene, encadena logs con el edge
 *     onTokenizado={(cardTokenId, payerId) => { ... }}
 *     onError={(msg) => { ... }}
 *   />
 *
 * Requisitos:
 *   - `mp_susc_public_key_prod` (o _test según MP_AMBIENTE) cargada en app_config.
 *   - MP_AMBIENTE en app_config define qué public key usar.
 *
 * Docs MP: https://www.mercadopago.com.uy/developers/es/docs/checkout-bricks/card-payment-brick/introduction
 */
export default function MpTarjetaBrick({ monto, emailPagador, userId, sessionId, firstName, lastName, identification, onTokenizado, onError }) {
  const brickContainerRef = useRef(null)
  const brickInstanceRef = useRef(null)
  // Si el parent pasó sessionId (wizard de signup, task #7 · 18-jul-2026), lo usamos para
  // encadenar con signup_errors_log del edge. Si no viene, generamos uno local (útil para
  // el flow de cambio de tarjeta desde CargarTarjeta.jsx donde no hay wizard).
  const sessionIdRef = useRef(sessionId || generarSessionId())
  const [cargando, setCargando] = useState(true)
  const [errorLocal, setErrorLocal] = useState(null)
  const [ready, setReady] = useState(false)  // true cuando Brick emitió onReady

  // Helper: POST fire-and-forget al endpoint de logging. Nunca rompe el flujo.
  const logFront = (paso, opts = {}) => {
    try {
      const body = {
        paso,                                              // ej: 'front_tokenize_failed'
        nivel: opts.nivel || 'error',
        email: emailPagador || null,
        user_id: userId || null,
        session_id: sessionIdRef.current,
        error_code: opts.error_code || null,
        error_msg: (opts.error_msg || '').slice(0, 2000) || null,
        payload: opts.payload || null,
      }
      // fire-and-forget: no await, no bloqueamos. Si falla, no importa.
      fetch(LOG_FRONT_ERROR_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,  // sobrevive a navegación/cierre de tab
      }).catch(() => {})
    } catch {}
  }

  useEffect(() => {
    let cancelado = false

    // Fallback hardcodeado — MP public keys son públicas por definición (van en
    // el HTML de cualquier checkout MP). Sirve como red de seguridad si el fetch
    // a app_config falla (RLS/sesión rota) para que el Brick nunca quede sin key.
    const FALLBACK_PUBLIC_KEY_PROD = 'APP_USR-a3651db0-30c9-4585-bc41-6ee937b0746f'
    const FALLBACK_AMBIENTE = 'prod'

    async function inicializar() {
      try {
        // 1. Traer public key + ambiente desde app_config (best-effort)
        const { data: cfg, error: cfgErr } = await supabase
          .from('app_config')
          .select('key, value')
          .in('key', ['MP_AMBIENTE', 'mp_susc_public_key_prod', 'mp_susc_public_key_test'])
        if (cfgErr) {
          console.warn('[MpTarjetaBrick] fetch app_config falló, uso fallback:', cfgErr)
          logFront('front_config_fetch_failed', { nivel: 'info', error_code: cfgErr.code, error_msg: cfgErr.message })
        }
        const map = {}
        for (const row of cfg || []) {
          let v = row.value
          try { v = JSON.parse(v) } catch {}
          map[row.key] = String(v).trim()
        }
        const ambiente = (map.MP_AMBIENTE || FALLBACK_AMBIENTE).toLowerCase()
        let publicKey = ambiente === 'prod' ? map.mp_susc_public_key_prod : map.mp_susc_public_key_test
        // Si el fetch no trajo la key, uso el fallback hardcodeado (solo para prod).
        if (!publicKey && ambiente === 'prod') {
          publicKey = FALLBACK_PUBLIC_KEY_PROD
          console.warn('[MpTarjetaBrick] usando public key hardcodeada (fallback prod)')
          logFront('front_public_key_fallback', { nivel: 'info' })
        }
        if (!publicKey) {
          logFront('front_public_key_missing', { error_msg: 'MP public key no configurada para ambiente ' + ambiente })
          throw new Error('MP public key no configurada')
        }

        // 2. Cargar el SDK MP JS (si no está) con timeout duro
        if (!window.MercadoPago) {
          try {
            await Promise.race([
              cargarScript('https://sdk.mercadopago.com/js/v2'),
              new Promise((_, rej) => setTimeout(() => rej(new Error('SDK MP timeout ' + SDK_LOAD_TIMEOUT_MS + 'ms')), SDK_LOAD_TIMEOUT_MS)),
            ])
          } catch (e) {
            logFront('front_sdk_load_failed', { error_code: 'sdk_timeout_or_network', error_msg: e.message })
            throw e
          }
        }
        if (cancelado) return
        if (!window.MercadoPago) {
          logFront('front_sdk_not_available', { error_msg: 'window.MercadoPago no existe post-load' })
          throw new Error('SDK MP no disponible después de cargar')
        }

        // 3. Instanciar MP con la public key
        const mp = new window.MercadoPago(publicKey, { locale: 'es-UY' })
        const bricksBuilder = mp.bricks()

        // 4. Render CardPayment Brick
        // Payer completo pre-carga los campos del formulario y refuerza el score antifraude MP UY.
        // MP JS Brick no acepta strings vacíos ("") en firstName/lastName → sólo mandamos si existe.
        const payerInicial = { email: emailPagador || '' }
        if (firstName) payerInicial.firstName = firstName
        if (lastName) payerInicial.lastName = lastName
        if (identification?.type && identification?.number) payerInicial.identification = identification
        brickInstanceRef.current = await bricksBuilder.create('cardPayment', 'mp-tarjeta-brick', {
          initialization: {
            amount: monto,
            payer: payerInicial,
          },
          customization: {
            visual: {
              style: { theme: 'default' },
              hideFormTitle: true,
              hidePaymentButton: false,
              texts: {
                // Es un preapproval (autoriza + guarda tarjeta). El cobro real
                // es D-1 de la primera entrega. "Pagar" (default) es engañoso.
                formSubmit: 'Guardar tarjeta y activar suscripción',
              },
            },
            paymentMethods: {
              maxInstallments: 1,  // suscripción → 1 pago mensual, sin cuotas
            },
          },
          callbacks: {
            onReady: () => {
              if (!cancelado) {
                setCargando(false)
                setReady(true)
                logFront('front_brick_ready', { nivel: 'info' })
              }
            },
            onError: (err) => {
              console.warn('MP Brick error:', err)
              // Logueamos SIEMPRE el error del Brick — este era el punto ciego que rompió a Marcela.
              // El SDK tira errores en tokenización, red, tarjeta inválida, etc y hasta ahora
              // solo se registraban en consola (invisible en prod).
              logFront('front_brick_onerror', {
                error_code: err?.type || err?.cause || 'brick_error',
                error_msg: err?.message || String(err),
                payload: {
                  type: err?.type || null,
                  cause: err?.cause || null,
                  fieldErrors: err?.field_errors || null,
                },
              })
              if (!cancelado) {
                const msg = err?.message || 'No pudimos validar la tarjeta. Revisá los datos o probá con otra.'
                setErrorLocal(msg)
                onError?.(msg)
              }
            },
            onSubmit: async (cardData, additionalData) => {
              // BUG-FIX 2026-07-09: la firma real del SDK MP JS es `(cardData, additionalData)`,
              // no `({ formData })`. Al destructurar mal, cardTokenId era SIEMPRE undefined
              // y el flow moría en `if (!cardTokenId)` sin llegar al preapproval. Ref:
              // https://github.com/mercadopago/sdk-js/blob/main/docs/bricks/card-payment.md
              const cardTokenId = cardData?.token
              const payerId = cardData?.payer?.id || null
              // P0 antifraude (2026-07-22): Device ID de MP. Lo genera el security.js del index.html
              // en window.MP_DEVICE_SESSION_ID. Sin esta señal, MP marcaba los pagos como
              // cc_rejected_high_risk. Se pasa al backend para el header X-meli-session-id.
              const deviceSessionId = (typeof window !== 'undefined' && window.MP_DEVICE_SESSION_ID) || null
              // Debug SOLO en DEV — en producción loguearíamos PII (identification.number,
              // lastFourDigits, cardholderName, payer.email). Ley UY 18.331 de proteccion
              // de datos personales.
              if (import.meta.env.DEV) {
                console.log('[MP Brick] onSubmit cardData:', {
                  hasToken: !!cardTokenId,
                  token: cardTokenId ? cardTokenId.slice(0, 8) + '...' : null,
                  paymentMethodId: cardData?.payment_method_id,
                  issuerId: cardData?.issuer_id,
                  installments: cardData?.installments,
                  payerEmail: cardData?.payer?.email,
                  identification: cardData?.payer?.identification,
                  lastFourDigits: additionalData?.lastFourDigits,
                  cardholderName: additionalData?.cardholderName,
                  keys: Object.keys(cardData || {}),
                })
              }
              if (!cardTokenId) {
                // Este es EL caso de Marcela: apretó "Guardar tarjeta", el SDK no generó token
                // por alguna razón (tarjeta rechazada por el bin, campos inválidos, timeout, etc)
                // y hasta ahora quedaba SOLO en pantalla como mensaje al user. Ahora también loguea.
                logFront('front_tokenize_no_token', {
                  error_code: 'no_card_token',
                  error_msg: 'onSubmit ejecutó pero cardData.token vino null/undefined',
                  payload: {
                    payment_method_id: cardData?.payment_method_id || null,
                    issuer_id: cardData?.issuer_id || null,
                    has_payer: !!cardData?.payer,
                    keys: Object.keys(cardData || {}),
                    bin: additionalData?.bin || null,  // los primeros 6 dígitos, útiles para debugging sin PII
                  },
                })
                onError?.('No pudimos validar la tarjeta. Revisá los datos o probá con otra.')
                return
              }
              // Validación P0: log de captura del Device ID (presencia, no el valor → sin PII).
              logFront('front_tokenize_ok', { nivel: 'info', payload: { bin: additionalData?.bin || null, device_id_ok: !!deviceSessionId } })
              if (!deviceSessionId) {
                // Señal de monitoreo: si el security.js no cargó, el device id no está y el
                // pago tiene más chance de high_risk. Queda registrado para diagnóstico.
                logFront('front_device_id_missing', { nivel: 'warn', error_msg: 'window.MP_DEVICE_SESSION_ID vacío al tokenizar' })
              }
              onTokenizado?.(cardTokenId, payerId, cardData, deviceSessionId)
            },
          },
        })
      } catch (e) {
        console.error('Error inicializando MpTarjetaBrick:', e)
        if (!cancelado) {
          setErrorLocal(e.message)
          setCargando(false)
          onError?.(e.message)
        }
      }
    }

    inicializar()

    return () => {
      cancelado = true
      if (brickInstanceRef.current) {
        try { brickInstanceRef.current.unmount() } catch {}
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monto, emailPagador])

  return (
    <div className="mp-tarjeta-brick-wrap">
      {cargando && (
        <div className="mp-brick-loading">Cargando pasarela de pago…</div>
      )}
      {errorLocal && (
        <div className="mp-brick-error">Error: {errorLocal}</div>
      )}
      <div id="mp-tarjeta-brick" ref={brickContainerRef} />
      <style>{`
        .mp-tarjeta-brick-wrap { min-height: 380px; position: relative }
        .mp-brick-loading { padding: 24px; text-align: center; color: #64748B; font-size: 14px }
        .mp-brick-error { padding: 12px 14px; background: #FEE2E2; color: #991B1B; border-radius: 8px; font-size: 13px; margin-bottom: 12px }
        #mp-tarjeta-brick { width: 100% }
      `}</style>
    </div>
  )
}

function cargarScript(src) {
  return new Promise((resolve, reject) => {
    if ([...document.scripts].some(s => s.src === src)) return resolve()
    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.onload = resolve
    s.onerror = () => reject(new Error(`No pude cargar ${src}`))
    document.head.appendChild(s)
  })
}
